import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, BookOpen, Calculator, Dumbbell, HeartHandshake, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AnimatedSection, SectionBadge } from './shared';

/**
 * CalculatorSEOContent — Contenu éducatif SEO sous le calculateur de calories.
 * Objectif: capter des mots-clés "calculateur calories macros", "périménopause", "protéines",
 * "recomposition corporelle" + créer une transition naturelle vers la consultation.
 */
const FAQ_BLOCKS = [
  {
    q: "Comment calculer ses calories quotidiennes ?",
    a: "Le calcul repose sur la formule scientifique Mifflin-St Jeor qui prend en compte l'âge, le sexe, la taille et le poids pour estimer ton métabolisme de base (BMR). On multiplie ensuite par un facteur d'activité (sédentaire à très actif) pour obtenir tes besoins totaux (TDEE). C'est exactement ce que fait le calculateur Body-Up, gratuitement et en 2 minutes.",
  },
  {
    q: "Quelle est la différence entre perte de gras, prise de muscle, recomposition et maintien ?",
    a: "Perte de gras : déficit calorique contrôlé pour faire baisser ta masse grasse en préservant le muscle. Prise de muscle : surplus calorique progressif avec protéines élevées pour construire du muscle. Recomposition corporelle : zone iso-calorique + protéines hautes + musculation, le corps perd du gras tout en construisant du muscle. Maintien : équilibre calorique pour stabiliser tes acquis.",
  },
  {
    q: "Combien de protéines par jour pour la perte de poids ?",
    a: "Lors d'un déficit calorique, on recommande généralement entre 1,6 et 2,2 g de protéines par kilo de poids corporel. Cette quantité protège ta masse musculaire pendant la perte de gras et améliore la satiété. Le calculateur Body-Up adapte automatiquement ce ratio selon ton objectif.",
  },
  {
    q: "Comment répartir glucides, lipides et protéines ?",
    a: "La répartition dépend de ton objectif et ton activité. Pour la perte de gras, on privilégie souvent : 30-35% protéines, 25-30% lipides, 35-40% glucides. Pour la prise de muscle : protéines maintenues, plus de glucides pour fueler l'entraînement. Les lipides ne doivent jamais descendre sous 0,8 g/kg pour la santé hormonale.",
  },
  {
    q: "Pourquoi les besoins changent chez les femmes en périménopause ou ménopause ?",
    a: "Les variations hormonales (baisse d'œstrogènes) modifient la sensibilité à l'insuline, le métabolisme de base et la composition corporelle. Beaucoup de femmes constatent une prise de gras abdominal et une fonte musculaire. La priorité devient alors : protéines suffisantes (≥ 1,8 g/kg), musculation régulière, sommeil de qualité et gestion du stress, plutôt qu'un simple déficit calorique.",
  },
  {
    q: "Pourquoi le calculateur reste une estimation ?",
    a: "Aucune formule ne peut tenir compte parfaitement de ta génétique, ton historique sportif, ton stress, ton sommeil ou ton équilibre hormonal. Le calculateur donne un excellent point de départ chiffré, mais c'est l'observation sur 2-3 semaines (énergie, performance, balance, mesures) qui permet d'ajuster avec précision. C'est exactement ce qu'on fait dans le coaching Body-Up.",
  },
  {
    q: "Quand se faire accompagner par un coach plutôt que de calculer seul ?",
    a: "Si tu plafonnes depuis plusieurs mois, si tu enchaînes les régimes sans résultat durable, si tu manques de motivation seul(e), ou si tu as des contraintes spécifiques (périménopause, douleurs, agenda chargé) — c'est le moment de te faire accompagner. Un coach apporte la stratégie, l'ajustement constant et le cadre humain qui rendent les résultats durables.",
  },
  {
    q: "Pourquoi un plan personnalisé est plus efficace qu'un simple chiffre calorique ?",
    a: "Un chiffre te donne la cible. Un plan personnalisé te donne le chemin : quels aliments choisir selon tes goûts et contraintes, comment gérer les écarts, comment ajuster en cas de plateau, comment intégrer ton entraînement et ta récupération. C'est l'écart entre « savoir » et « réussir ».",
  },
];

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-white/10 last:border-b-0" data-testid={`calc-faq-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 py-5 text-left hover:text-primary transition-colors"
        aria-expanded={open}
      >
        <span className="font-semibold text-white text-base">{q}</span>
        <ChevronDown className={`w-5 h-5 text-zinc-400 shrink-0 transition-transform ${open ? 'rotate-180 text-primary' : ''}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-zinc-300 text-sm leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const CalculatorSEOContent = ({ language = 'fr' }) => {
  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);

  return (
    <section className="py-16 bg-gradient-to-b from-black to-zinc-950 relative overflow-hidden" data-testid="calc-seo-content">
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Intro éducative */}
        <AnimatedSection>
          <div className="text-center mb-12">
            <SectionBadge emoji="📚">{L('TOUT COMPRENDRE', 'LEARN MORE')}</SectionBadge>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {L('Calculateur ', 'Calculator ')}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-500 to-yellow-400">
                {L('calories & macros', 'calories & macros')}
              </span>
              <span className="block text-xl md:text-2xl font-normal text-zinc-400 mt-2">
                {L('Comprendre ce que dit ton résultat', 'Understanding what your result means')}
              </span>
            </h2>
          </div>
        </AnimatedSection>

        {/* 3 quick concepts cards */}
        <AnimatedSection delay={100}>
          <div className="grid md:grid-cols-3 gap-4 mb-10">
            {[
              { icon: Calculator, title: L('Mifflin-St Jeor', 'Mifflin-St Jeor'), desc: L('Formule scientifique de référence pour estimer ton métabolisme.', 'Scientific reference formula for your metabolism.') },
              { icon: Dumbbell, title: L('Macros adaptés', 'Tailored macros'), desc: L('Protéines, glucides et lipides ajustés selon ton objectif.', 'Protein, carbs and fats tailored to your goal.') },
              { icon: HeartHandshake, title: L('Suivi humain', 'Human follow-up'), desc: L('Un chiffre ne suffit pas — un coach ajuste sur la durée.', "A number isn't enough — a coach adjusts over time.") },
            ].map((it, i) => {
              const Ico = it.icon;
              return (
                <div key={i} className="rounded-2xl border border-white/10 bg-zinc-900/40 p-5">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center mb-3">
                    <Ico className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-base font-bold text-white mb-1">{it.title}</h3>
                  <p className="text-xs text-zinc-400 leading-relaxed">{it.desc}</p>
                </div>
              );
            })}
          </div>
        </AnimatedSection>

        {/* FAQ accordion */}
        <AnimatedSection delay={200}>
          <div className="rounded-3xl border border-white/10 bg-zinc-900/40 backdrop-blur px-6 py-2">
            {FAQ_BLOCKS.map((it, i) => (
              <FaqItem key={i} q={it.q} a={it.a} idx={i} />
            ))}
          </div>
        </AnimatedSection>

        {/* CTA final → consultation gratuite */}
        <AnimatedSection delay={300}>
          <div className="mt-10 rounded-3xl border border-primary/40 bg-gradient-to-br from-primary/15 via-zinc-900 to-black p-8 text-center overflow-hidden relative">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-primary/30 rounded-full blur-3xl" />
            <div className="relative">
              <BookOpen className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {L('Tu veux que Laurent analyse ton résultat', 'Want Laurent to analyze your result')}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-yellow-400">
                  {L('et te dise quoi faire concrètement ?', 'and tell you exactly what to do next?')}
                </span>
              </h3>
              <p className="text-zinc-300 text-sm max-w-xl mx-auto mb-6">
                {L(
                  "Calculateur + plan personnalisé = la combinaison gagnante. Réserve ta consultation gratuite (sans engagement) pour transformer ces chiffres en résultats concrets.",
                  "Calculator + personalized plan = winning combo. Book your free consultation (no commitment) to turn these numbers into concrete results."
                )}
              </p>
              <Link to="/contact" data-testid="calc-seo-cta">
                <button className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-primary via-pink-500 to-purple-500 text-white font-bold shadow-2xl shadow-primary/40 hover:scale-105 transition-transform">
                  {L('Réserver ma consultation gratuite', 'Book my free consultation')}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </Link>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default CalculatorSEOContent;
